import React, { useEffect, useMemo, useState } from 'react';
import {
    Table,
    Select,
    DatePicker,
    Input,
    Button,
    Space,
    Tag,
    Drawer,
    Descriptions,
    Empty,
    Divider,
    Typography,
    Spin,
    Popconfirm,
    Alert,
} from 'antd';
import {
    EyeOutlined,
    ReloadOutlined,
    ClearOutlined,
    UndoOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { useArchive } from '../../store/archiveContext';
import { useAuth } from '../../store/AuthContext';
import { PERMS } from '../../constants/permissions';
import { ARCHIVE_STATUSES, ArchiveRecord, ArchiveStatus } from '../../types/archive';
import PageNavigation from '../Layout/PageNavigation';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const statusColors: Record<ArchiveStatus, string> = {
    archived: 'red',
    restored: 'green',
};

function formatDateTime(value?: string | null): string {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

const ArchiveList: React.FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = useAuth();
    const {
        records,
        pagination,
        summary,
        restorableModels,
        loading,
        working,
        fetchArchives,
        fetchSummary,
        fetchArchiveById,
        restore,
        restoreSelected,
        purge,
    } = useArchive();

    const [models, setModels] = useState<string[]>([]);
    const [statuses, setStatuses] = useState<ArchiveStatus[]>([]);
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [search, setSearch] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

    const [selected, setSelected] = useState<ArchiveRecord | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    const canRestore = hasPermission(PERMS.archive.restore);
    const canPurge = hasPermission(PERMS.archive.delete);

    // Model name as stored on the record ("SellInvoice"), translated when a label exists.
    const modelLabel = (name: string) => t(`archive_model_${name}`, { defaultValue: name }) as string;

    const query = useMemo(() => ({
        collectionName: models,
        status: statuses,
        search: search || undefined,
        from: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
        to: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
        page,
        limit,
        sort: 'desc' as const,
    }), [models, statuses, search, range, page, limit]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    useEffect(() => {
        fetchArchives(query);
    }, [query, fetchArchives]);

    const refresh = () => {
        setSelectedKeys([]);
        fetchSummary();
        fetchArchives(query);
    };

    const modelOptions = useMemo(
        () => summary.map(item => ({
            value: item.collectionName,
            label: `${modelLabel(item.collectionName)} (${item.total})`,
        })),
        [summary, t] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const statusOptions = useMemo(
        () => ARCHIVE_STATUSES.map(status => ({
            value: status,
            label: t(`archive_status_${status}`),
        })),
        [t]
    );

    const resetFilters = () => {
        setModels([]);
        setStatuses([]);
        setRange(null);
        setSearch('');
        setSearchInput('');
        setPage(1);
        setSelectedKeys([]);
    };

    /** A record can only go back if it is still archived and its model is known. */
    const isRestorable = (record: ArchiveRecord) =>
        record.status === 'archived'
        && (restorableModels.length === 0 || restorableModels.includes(record.collectionName));

    const openDrawer = async (record: ArchiveRecord) => {
        // The list response strips the snapshot, so it is fetched on demand.
        setSelected(record);
        setDrawerOpen(true);
        setDetailLoading(true);
        const full = await fetchArchiveById(record.id);
        if (full) setSelected(full);
        setDetailLoading(false);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelected(null);
    };

    const handleRestore = async (record: ArchiveRecord) => {
        const ok = await restore(record.id);
        if (ok) {
            closeDrawer();
            refresh();
        }
    };

    const handleRestoreSelected = async () => {
        const result = await restoreSelected(selectedKeys.map(String));
        if (result) refresh();
    };

    const handlePurge = async (record: ArchiveRecord) => {
        const ok = await purge(record.id);
        if (ok) {
            closeDrawer();
            refresh();
        }
    };

    /** Rows already restored, or of a model the API cannot rebuild, stay unselectable. */
    const selectableKeys = useMemo(
        () => records.filter(isRestorable).map(record => record.id),
        [records, restorableModels] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const columns = [
        {
            title: t('archive_deleted_at'),
            dataIndex: 'deletedAt',
            render: (value: string) => formatDateTime(value),
        },
        {
            title: t('archive_model'),
            dataIndex: 'collectionName',
            render: (name: string) => <Tag color="geekblue">{modelLabel(name)}</Tag>,
        },
        {
            title: t('archive_label'),
            dataIndex: 'label',
            render: (label?: string) => label || <Text type="secondary">-</Text>,
        },
        {
            title: t('archive_status'),
            dataIndex: 'status',
            render: (status: ArchiveStatus) => (
                <Tag color={statusColors[status] || 'default'}>
                    {t(`archive_status_${status}`, { defaultValue: status })}
                </Tag>
            ),
        },
        {
            title: t('archive_deleted_by'),
            dataIndex: ['deletedBy', 'name'],
            render: (_: any, record: ArchiveRecord) => (
                <Space direction="vertical" size={0}>
                    <span>{record.deletedBy?.name || '-'}</span>
                    {record.deletedBy?.role && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.deletedBy.role}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: t('archive_restored_at'),
            dataIndex: 'restoredAt',
            render: (value: string | null, record: ArchiveRecord) =>
                record.status === 'restored'
                    ? (
                        <Space direction="vertical" size={0}>
                            <span>{formatDateTime(value)}</span>
                            {record.restoredBy?.name && (
                                <Text type="secondary" style={{ fontSize: 12 }}>{record.restoredBy.name}</Text>
                            )}
                        </Space>
                    )
                    : <Text type="secondary">-</Text>,
        },
        {
            title: t('archive_record_id'),
            dataIndex: 'documentId',
            render: (id: string) => (
                <Text copyable={!!id} style={{ fontSize: 12 }}>{id || '-'}</Text>
            ),
        },
        {
            title: t('actions'),
            render: (_: any, record: ArchiveRecord) => (
                <Space wrap>
                    <Button icon={<EyeOutlined />} onClick={() => openDrawer(record)}>
                        {t('archive_details')}
                    </Button>

                    {canRestore && (
                        <Popconfirm
                            title={t('archive_restore_confirm_title')}
                            description={t('archive_restore_confirm_text')}
                            okText={t('archive_restore')}
                            cancelText={t('cancel')}
                            disabled={!isRestorable(record)}
                            onConfirm={() => handleRestore(record)}
                        >
                            <Button
                                type="primary"
                                icon={<UndoOutlined />}
                                loading={working}
                                disabled={!isRestorable(record)}
                            >
                                {t('archive_restore')}
                            </Button>
                        </Popconfirm>
                    )}

                    {canPurge && (
                        <Popconfirm
                            title={t('archive_purge_confirm_title')}
                            description={t('archive_purge_confirm_text')}
                            okText={t('delete')}
                            okButtonProps={{ danger: true }}
                            cancelText={t('cancel')}
                            onConfirm={() => handlePurge(record)}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={working}>
                                {t('archive_purge')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <PageNavigation title={t('archive') as string} />

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('archive_intro')}
            />

            <Space wrap style={{ marginBottom: 16 }} size="middle">
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 240 }}
                    placeholder={t('archive_all_models')}
                    value={models}
                    options={modelOptions}
                    onChange={values => { setModels(values); setPage(1); setSelectedKeys([]); }}
                    maxTagCount="responsive"
                />
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 200 }}
                    placeholder={t('archive_all_statuses')}
                    value={statuses}
                    options={statusOptions}
                    onChange={values => { setStatuses(values); setPage(1); setSelectedKeys([]); }}
                    maxTagCount="responsive"
                />
                <RangePicker
                    value={range}
                    onChange={dates => {
                        setRange(dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null);
                        setPage(1);
                    }}
                />
                <Input.Search
                    allowClear
                    style={{ width: 260 }}
                    placeholder={t('archive_search_ph') as string}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onSearch={value => { setSearch(value.trim()); setPage(1); }}
                />
                <Button icon={<ClearOutlined />} onClick={resetFilters}>
                    {t('archive_reset_filters')}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={refresh}>
                    {t('archive_refresh')}
                </Button>
                {canRestore && (
                    <Popconfirm
                        title={t('archive_restore_confirm_title')}
                        description={t('archive_restore_selected_text', { total: selectedKeys.length })}
                        okText={t('archive_restore')}
                        cancelText={t('cancel')}
                        disabled={!selectedKeys.length}
                        onConfirm={handleRestoreSelected}
                    >
                        <Button
                            type="primary"
                            icon={<UndoOutlined />}
                            disabled={!selectedKeys.length}
                            loading={working}
                        >
                            {t('archive_restore_selected')} ({selectedKeys.length})
                        </Button>
                    </Popconfirm>
                )}
            </Space>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={records}
                columns={columns}
                scroll={{ x: 'max-content' }}
                rowSelection={canRestore ? {
                    selectedRowKeys: selectedKeys,
                    onChange: keys => setSelectedKeys(keys),
                    getCheckboxProps: (record: ArchiveRecord) => ({
                        disabled: !selectableKeys.includes(record.id),
                    }),
                } : undefined}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: total => `${t('archive_total')}: ${total}`,
                    onChange: (nextPage, nextSize) => {
                        setPage(nextSize !== limit ? 1 : nextPage);
                        setLimit(nextSize);
                        setSelectedKeys([]);
                    },
                }}
                locale={{ emptyText: <Empty description={t('archive_empty')} /> }}
            />

            <Drawer
                title={selected
                    ? `${modelLabel(selected.collectionName)}${selected.label ? ` — ${selected.label}` : ''}`
                    : t('archive_details')}
                placement="right"
                width={560}
                open={drawerOpen}
                onClose={closeDrawer}
                extra={selected && canRestore && isRestorable(selected) ? (
                    <Popconfirm
                        title={t('archive_restore_confirm_title')}
                        description={t('archive_restore_confirm_text')}
                        okText={t('archive_restore')}
                        cancelText={t('cancel')}
                        onConfirm={() => handleRestore(selected)}
                    >
                        <Button type="primary" icon={<UndoOutlined />} loading={working}>
                            {t('archive_restore')}
                        </Button>
                    </Popconfirm>
                ) : undefined}
            >
                <Spin spinning={detailLoading}>
                    {selected ? (
                        <>
                            <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label={t('archive_model')}>
                                    <Tag color="geekblue">{modelLabel(selected.collectionName)}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_status')}>
                                    <Tag color={statusColors[selected.status] || 'default'}>
                                        {t(`archive_status_${selected.status}`, { defaultValue: selected.status })}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_label')}>
                                    {selected.label || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_record_id')}>
                                    <Text copyable={!!selected.documentId}>{selected.documentId || '-'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_deleted_at')}>
                                    {formatDateTime(selected.deletedAt)}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_deleted_by')}>
                                    {selected.deletedBy?.name || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_restored_at')}>
                                    {selected.status === 'restored' ? formatDateTime(selected.restoredAt) : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('archive_restored_by')}>
                                    {selected.restoredBy?.name || '-'}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider orientation="left" style={{ marginTop: 20 }}>
                                {t('archive_snapshot')}
                            </Divider>
                            {selected.data ? (
                                <pre
                                    style={{
                                        background: '#f5f5f5',
                                        padding: 12,
                                        borderRadius: 8,
                                        maxHeight: 380,
                                        overflow: 'auto',
                                        fontSize: 12,
                                        direction: 'ltr',
                                        textAlign: 'left',
                                    }}
                                >
                                    {JSON.stringify(selected.data, null, 2)}
                                </pre>
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('archive_no_snapshot')} />
                            )}
                        </>
                    ) : (
                        <Empty description={t('archive_no_selected')} />
                    )}
                </Spin>
            </Drawer>
        </div>
    );
};

export default ArchiveList;
